/*

  LeaderboardView.ts

  Daily leaderboard display component for Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { Component, m } from "./mithril";
import { LeaderboardEntry } from "./types";
import { glyph } from "./util";

interface ILeaderboardViewAttributes {
  leaderboard: LeaderboardEntry[];
  currentUserId: string;
  date: string;
  loading?: boolean;
}

function getMedalIcon(rank: number): string | null {
  switch (rank) {
    case 1: return "🥇";
    case 2: return "🥈";
    case 3: return "🥉";
    default: return null;
  }
}

function formatDate(dateStr: string): string {
  // Format YYYY-MM-DD to Icelandic date (e.g., "2. okt.")
  const date = new Date(dateStr + "T00:00:00");
  const day = date.getDate();
  const months = [
    "janúar", "febrúar", "mars", "apríl", "maí", "júní",
    "júlí", "ágúst", "september", "október", "nóvember", "desember",
  ];
  const month = months[date.getMonth()];
  return `${day}. ${month}`;
}

export const LeaderboardView: Component<ILeaderboardViewAttributes> = {
  view: (vnode) => {
    const { leaderboard, currentUserId, date, loading = false } = vnode.attrs;

    if (loading) {
      return m(".leaderboard-view.loading", m(".loading-message", ts("Hleð stigatöflu...")));
    }

    if (!leaderboard || leaderboard.length === 0) {
      return m(".leaderboard-view.empty", m(".empty-message", ts("Engin stig skráð enn")));
    }

    return m(".leaderboard-view", [
      m(".leaderboard-header", [
        m(".leaderboard-title", formatDate(date)),
      ]),
      m(".leaderboard-list",
        {
          // Allow touch scrolling but prevent events from bubbling to backdrop
          ontouchmove: (e: Event) => { e.stopPropagation(); }
        },
        leaderboard.map((entry, index) => {
          const rank = index + 1;
          const isCurrentUser = entry.userId === currentUserId;
          const medal = getMedalIcon(rank);

          return m(
            ".leaderboard-entry" + (isCurrentUser ? ".current-user" : ""),
            { key: entry.userId },
            [
              m(".entry-rank", [
                medal ? m("span.medal", medal) : m("span.rank-number", rank.toString())
              ]),
              m(".entry-name",
                isCurrentUser
                  ? [ m("span.entry-star", glyph("star")), ts("Þú") ]
                  : entry.displayName
              ),
              m(".entry-score", entry.score.toString())
            ]
          );
        })
      )
    ]);
  }
};
